import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import { useI18n } from '../i18n';
import { useCan } from '../hooks/useMe';
import { dayValue } from '../utils/dates';
import api from '../services/api';

// El número se guarda como lo capturaron. Para abrir WhatsApp hace falta la
// lada de país y en México el número nacional ya trae 10 dígitos: con diez o
// menos no hay forma de saber a qué país llamar, así que se muestra el texto
// tal cual en vez de mandar el mensaje a otro lado.
export function waLink(whatsapp) {
  const digits = (whatsapp || '').replace(/\D/g, '');
  return digits.length >= 11 ? `https://wa.me/${digits}` : null;
}

const EMPTY = { name: '', email: '', whatsapp: '', job_title: '' };

// El seguimiento viaja como null / true / false; el select necesita cadenas.
const RESPONDED_VALUE = { null: '', true: 'yes', false: 'no' };
const RESPONDED_FROM = { '': null, yes: true, no: false };

const trackingInput = {
  fontSize: 11.5, fontFamily: 'inherit', color: '#4A3F47',
  border: '1px solid #E4E0E2', borderRadius: '4px', backgroundColor: '#fff',
  px: 0.5, py: '1px', height: 22,
  '&:disabled': { backgroundColor: 'transparent', borderColor: 'transparent', color: '#847782' },
};

// Último contacto, si respondió y si agendó sesión. Cada cambio se guarda solo;
// la fecha, al salir del campo, porque mientras alguien teclea el año el input
// ya reporta fechas válidas como 0002-10-07.
function ContactTracking({ contact, disabled, onSave, t }) {
  const initial = contact.last_contacted_at ? dayValue(contact.last_contacted_at) : '';
  const [day, setDay] = useState(initial);
  useEffect(() => setDay(initial), [initial]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap', mt: 0.6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: '#847782' }}>{t('contacts.lastContact')}</Typography>
        <Box
          component="input" type="date" value={day} disabled={disabled}
          onChange={(e) => setDay(e.target.value)}
          onBlur={() => { if (day !== initial) onSave({ last_contacted_at: day || null }); }}
          sx={trackingInput}
        />
        {!disabled && day !== dayValue(new Date()) && (
          <Box
            component="button" onClick={() => onSave({ last_contacted_at: dayValue(new Date()) })}
            sx={{
              border: 'none', background: 'none', cursor: 'pointer', p: 0, fontSize: 11,
              color: '#FF6666', fontFamily: 'inherit',
            }}
          >{t('contacts.today')}</Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: '#847782' }}>{t('contacts.responded')}</Typography>
        <Box
          component="select" disabled={disabled}
          value={RESPONDED_VALUE[String(contact.responded ?? null)]}
          onChange={(e) => onSave({ responded: RESPONDED_FROM[e.target.value] })}
          sx={{
            ...trackingInput,
            color: contact.responded === true ? '#4F7A5F'
              : contact.responded === false ? '#AE5B61' : '#4A3F47',
          }}
        >
          <option value="">{t('contacts.respondedUnknown')}</option>
          <option value="yes">{t('contacts.respondedYes')}</option>
          <option value="no">{t('contacts.respondedNo')}</option>
        </Box>
      </Box>

      <Box component="label" sx={{
        display: 'flex', alignItems: 'center', gap: 0.4, fontSize: 11,
        color: contact.session_scheduled ? '#4F7A5F' : '#847782',
        cursor: disabled ? 'default' : 'pointer',
      }}>
        <input
          type="checkbox" checked={Boolean(contact.session_scheduled)} disabled={disabled}
          onChange={(e) => onSave({ session_scheduled: e.target.checked })}
        />
        {t('contacts.sessionScheduled')}
      </Box>
    </Box>
  );
}

function ContactForm({ draft, setDraft, busy, onSave, onCancel, t }) {
  return (
    <Box sx={{ mb: 2, display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small" autoFocus sx={{ flexGrow: 1 }} placeholder={t('contacts.name')}
          value={draft.name} disabled={busy}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <TextField
          size="small" sx={{ flexGrow: 1 }} placeholder={t('contacts.jobTitle')}
          value={draft.job_title} disabled={busy}
          onChange={(e) => setDraft({ ...draft, job_title: e.target.value })}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small" type="email" sx={{ flexGrow: 1 }} placeholder={t('contacts.email')}
          value={draft.email} disabled={busy}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
        <TextField
          size="small" sx={{ flexGrow: 1 }} placeholder={t('contacts.whatsappHint')}
          value={draft.whatsapp} disabled={busy}
          onChange={(e) => setDraft({ ...draft, whatsapp: e.target.value })}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small" variant="contained" onClick={onSave}
          disabled={busy || !(draft.name.trim() || draft.email.trim() || draft.whatsapp.trim())}
        >{t('common.save')}</Button>
        <Button size="small" onClick={onCancel} sx={{ color: '#847782' }}>{t('common.close')}</Button>
      </Box>
    </Box>
  );
}

export default function AccountContacts({ tenantId, source }) {
  const { t } = useI18n();
  const canLog = useCan('log_activity');

  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/api/accounts/${tenantId}/contacts`, { params: { source } })
      .then((res) => setContacts(res.data))
      .catch(() => setError(t('contacts.loadError')));
  }, [tenantId, source, t]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy('form');
    try {
      if (editing) await api.patch(`/api/contacts/${editing}`, draft);
      else await api.post(`/api/accounts/${tenantId}/contacts`, draft, { params: { source } });
      setDraft(null);
      setEditing(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('contacts.saveError'));
    } finally {
      setBusy(null);
    }
  }

  async function track(contact, patch) {
    setBusy(contact.id);
    try {
      const { data } = await api.patch(`/api/contacts/${contact.id}`, patch);
      setContacts((list) => list.map((c) => (c.id === data.id ? data : c)));
    } catch (err) {
      setError(err.response?.data?.error || t('contacts.saveError'));
    } finally {
      setBusy(null);
    }
  }

  async function makePrimary(contact) {
    setBusy(contact.id);
    try {
      await api.patch(`/api/contacts/${contact.id}`, { is_primary: !contact.is_primary });
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('contacts.saveError'));
    } finally {
      setBusy(null);
    }
  }

  async function remove(contact) {
    if (!window.confirm(t('contacts.deleteConfirm'))) return;
    setBusy(contact.id);
    try {
      await api.delete(`/api/contacts/${contact.id}`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('contacts.deleteError'));
    } finally {
      setBusy(null);
    }
  }

  function edit(contact) {
    setEditing(contact.id);
    setDraft({
      name: contact.name || '',
      email: contact.email || '',
      whatsapp: contact.whatsapp || '',
      job_title: contact.job_title || '',
    });
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.2,
        fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: '#847782',
      }}>
        {t('contacts.title')}
        {contacts.length > 0 && (
          <Box component="span" sx={{ color: '#FF6666', fontSize: 13 }}>{contacts.length}</Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canLog && !draft && (
          <Button
            size="small" onClick={() => { setEditing(null); setDraft({ ...EMPTY }); }}
            sx={{ fontSize: 11.5, color: '#FF6666', textTransform: 'none', letterSpacing: 0 }}
          >{t('contacts.add')}</Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {draft && (
        <ContactForm
          draft={draft} setDraft={setDraft} busy={busy === 'form'} t={t}
          onSave={save} onCancel={() => { setDraft(null); setEditing(null); }}
        />
      )}

      {contacts.length === 0 && !draft && (
        <Typography sx={{ fontSize: 13.5, color: '#847782' }}>{t('contacts.empty')}</Typography>
      )}

      {contacts.map((contact) => {
        const link = waLink(contact.whatsapp);
        return (
          <Box key={contact.id} sx={{
            display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.9,
            borderBottom: '1px solid #F5F1F3', '&:last-of-type': { borderBottom: 'none' },
          }}>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                {/* Del machote llegan contactos sin nombre: entonces el correo
                    hace de título, y sigue sirviendo para escribirle. */}
                <Typography
                  {...(!contact.name && contact.email
                    ? { component: 'a', href: `mailto:${contact.email}` } : {})}
                  sx={{ fontSize: 13.5, fontWeight: 500, color: 'inherit', textDecoration: 'none',
                    '&:hover': { color: contact.name ? 'inherit' : '#FF6666' } }}
                >
                  {contact.name || contact.email || contact.whatsapp}
                </Typography>
                {contact.is_primary && (
                  <Box component="span" sx={{
                    fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em',
                    px: 0.7, py: '1px', borderRadius: '3px',
                    backgroundColor: '#F3E9F3', color: '#531253',
                  }}>{t('contacts.primary').toUpperCase()}</Box>
                )}
                {contact.job_title && (
                  <Typography sx={{ fontSize: 11.5, color: '#847782' }}>{contact.job_title}</Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1.4, flexWrap: 'wrap', mt: 0.2 }}>
                {contact.email && contact.name && (
                  <Typography
                    component="a" href={`mailto:${contact.email}`}
                    sx={{ fontSize: 11.5, color: '#847782', textDecoration: 'none', '&:hover': { color: '#FF6666' } }}
                  >{contact.email}</Typography>
                )}
                {contact.whatsapp && (link ? (
                  <Typography
                    component="a" href={link} target="_blank" rel="noopener noreferrer"
                    title={t('contacts.whatsappOpen')}
                    sx={{ fontSize: 11.5, color: '#4F7A5F', textDecoration: 'none', fontVariantNumeric: 'tabular-nums',
                      '&:hover': { textDecoration: 'underline' } }}
                  >{t('contacts.whatsapp')} {contact.whatsapp}</Typography>
                ) : (
                  // Sin lada no se arma la liga: se muestra el número para que
                  // alguien lo complete, no una liga que abriría otro chat.
                  <Tooltip title={t('contacts.noCountryCode')} placement="top" arrow>
                    <Typography sx={{
                      fontSize: 11.5, color: '#847782', cursor: 'help',
                      textDecoration: 'underline dotted', textUnderlineOffset: '3px',
                      textDecorationColor: '#C9BFC7', fontVariantNumeric: 'tabular-nums',
                    }}>{t('contacts.whatsapp')} {contact.whatsapp}</Typography>
                  </Tooltip>
                ))}
              </Box>

              <ContactTracking
                contact={contact} t={t}
                disabled={!canLog || busy === contact.id}
                onSave={(patch) => track(contact, patch)}
              />
            </Box>

            {canLog && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
                <Box
                  component="button" onClick={() => makePrimary(contact)} disabled={busy === contact.id}
                  title={contact.is_primary ? t('contacts.unsetPrimary') : t('contacts.makePrimary')}
                  sx={{
                    border: 'none', background: 'none', cursor: 'pointer', p: 0, fontSize: 13, lineHeight: 1,
                    color: contact.is_primary ? '#FF6666' : '#C9BFC7', fontFamily: 'inherit',
                    '&:hover': { color: '#FF6666' },
                  }}
                >★</Box>
                <Box
                  component="button" onClick={() => edit(contact)} disabled={busy === contact.id}
                  title={t('common.edit')}
                  sx={{
                    border: 'none', background: 'none', cursor: 'pointer', p: 0, fontSize: 11.5,
                    color: '#847782', fontFamily: 'inherit', '&:hover': { color: '#FF6666' },
                  }}
                >{t('common.edit')}</Box>
                <Box
                  component="button" onClick={() => remove(contact)} disabled={busy === contact.id}
                  title={t('common.delete')}
                  sx={{
                    border: 'none', background: 'none', cursor: 'pointer', p: 0, fontSize: 15, lineHeight: 1,
                    color: '#847782', fontFamily: 'inherit', '&:hover': { color: '#AE5B61' },
                  }}
                >×</Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
